/* eslint-disable arrow-parens, max-len */
import React, { useState, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Spinner } from 'reactstrap'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useToasts } from 'react-toast-notifications'
import userApi from '_api/user'
import orderApi from '_api/order'
import { getUserInfo } from '_utils/localData'

const PurchaseDetailAddAddressModal = ({
  isOpen,
  toggle,
  onAddressAdded,
  phoneNumber,
  userAddresses = [],
  orderRef,
  onAddressUpdated
}) => {
  const { addToast } = useToasts()
  const userInfo = getUserInfo()
  const [isLoading, setIsLoading] = useState(false)

  const hasNoAddresses = !userAddresses || userAddresses.length === 0

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Address name is required')
      .min(2, 'Address name must be at least 2 characters'),
    address: Yup.string()
      .required('Address is required')
      .min(3, 'Address must be at least 3 characters'),
    postalCode: Yup.string()
      .required('Postal code is required')
      .matches(/^\d{6}$/, 'Postal code must be 6 digits'),
    country: Yup.string()
      .required('Country is required'),
    phone: Yup.string()
      .required('Phone number is required')
      .min(8, 'Phone number must be at least 8 characters'),
    isDefault: Yup.boolean().default(false)
  })

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: 'Home',
      address: '',
      postalCode: '',
      country: 'Singapore',
      phone: phoneNumber || userInfo?.phone || '',
      isDefault: false
    }
  })

  useEffect(() => {
    const noAddresses = !userAddresses || userAddresses.length === 0
    setValue('isDefault', noAddresses)
  }, [userAddresses, setValue])

  useEffect(() => {
    if (isOpen) {
      const noAddresses = !userAddresses || userAddresses.length === 0
      reset({
        name: 'Home',
        address: '',
        postalCode: '',
        country: 'Singapore',
        phone: phoneNumber || userInfo?.phone || '',
        isDefault: noAddresses
      })
    }
  }, [isOpen, userAddresses, phoneNumber, userInfo?.phone, reset])

    const onSubmit = async (data) => {
    setIsLoading(true)

    try {
      const { address: addressStr, postalCode, country } = data
      const newBookingAddress = `${addressStr}, ${country}, ${postalCode}`

      if (orderRef) {
        await orderApi.updateOrderAddress(orderRef, {
          bookingAddress: newBookingAddress,
          phone: data.phone || userInfo?.phone || '',
          consignee: userInfo?.name || ''
        })

        if (onAddressUpdated) {
          onAddressUpdated(newBookingAddress)
        }
      }

      const dataToSend = {
        name: data.name,
        postalCode: data.postalCode,
        address: {
          street: data.address
        },
        consignee: userInfo?.name,
        country: data.country,
        phone: data.phone,
        isDefault: data.isDefault
      }

      userApi.createUserAddress(dataToSend).then(() => {
        if (onAddressAdded) {
          onAddressAdded()
        }
      }).catch((error) => {
        console.error('Failed to save address to profile:', error)
      })

      addToast('Delivery address updated successfully!', { appearance: 'success', autoDismiss: true })

      reset({
        name: 'Home',
        address: '',
        postalCode: '',
        country: 'Singapore',
        phone: userInfo?.phone || '',
        isDefault: hasNoAddresses
      })

      toggle()

      if (onAddressAdded) {
        onAddressAdded('CLOSE_PARENT_MODAL')
      }
    } catch (error) {
      addToast(error?.msgResp || 'Failed to update delivery address', { appearance: 'error', autoDismiss: true })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered style={{ maxWidth: '498px' }}>
      <ModalHeader toggle={handleClose}>Add Delivery Address</ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <FormGroup>
            <Label for="name" style={{ fontSize: '16px' }}>
              Address Label (e.g. Home, Office)<span style={{ color: 'red' }}>*</span>
            </Label>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="text"
                  id="name"
                  className="small-placeholder"
                  placeholder="e.g. Home, Office, etc."
                  invalid={!!fieldState.error}
                />
              )}
            />
            {errors.name && <div className="invalid-feedback d-block">{errors.name.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="address" style={{ fontSize: '16px' }}>
              Address <span style={{ color: 'red' }}>*</span>
            </Label>
            <Controller
              name="address"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="text"
                  id="address"
                  className="small-placeholder"
                  placeholder="Enter your full address"
                  invalid={!!fieldState.error}
                />
              )}
            />
            {errors.address && <div className="invalid-feedback d-block">{errors.address.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="postalCode" style={{ fontSize: '16px' }}>
              Postal Code <span style={{ color: 'red' }}>*</span>
            </Label>
            <Controller
              name="postalCode"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="text"
                  id="postalCode"
                  className="small-placeholder"
                  placeholder="Enter 6-digit postal code"
                  invalid={!!fieldState.error}
                  maxLength={6}
                />
              )}
            />
            {errors.postalCode && <div className="invalid-feedback d-block">{errors.postalCode.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="country" style={{ fontSize: '16px' }}>
              Country <span style={{ color: 'red' }}>*</span>
            </Label>
            <div style={{ position: 'relative' }}>
              <Controller
                name="country"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    type="select"
                    id="country"
                    className="small-placeholder custom-country-select"
                    invalid={!!fieldState.error}
                    disabled
                  >
                    <option value="Singapore">Singapore</option>
                  </Input>
                )}
              />
              <div
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  fontSize: '12px',
                  color: '#6c757d'
                }}
              >
                ▾
              </div>
            </div>
            {errors.country && <div className="invalid-feedback d-block">{errors.country.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="phone" style={{ fontSize: '16px' }}>
              Phone Number <span style={{ color: 'red' }}>*</span>
            </Label>
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="tel"
                  id="phone"
                  className="small-placeholder"
                  placeholder="Enter phone number"
                  invalid={!!fieldState.error}
                />
              )}
            />
            {errors.phone && <div className="invalid-feedback d-block">{errors.phone.message}</div>}
          </FormGroup>

          <FormGroup check>
            <Controller
              name="isDefault"
              control={control}
              render={({ field }) => (
                <Label check style={{ fontSize: '16px' }}>
                  <Input
                    type="checkbox"
                    {...field}
                    checked={!!field.value}
                    disabled={hasNoAddresses}
                  />
                  Set as default address
                  {hasNoAddresses && (
                    <small style={{ color: '#6c757d', marginLeft: '8px', display: 'block' }}>
                      (This will be your first and default address)
                    </small>
                  )}
                </Label>
              )}
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
            <Button color="light" onClick={handleClose} disabled={isLoading} style={{ border: '1px solid #07C3C3' }}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isLoading} style={{ borderRadius: '25px' }}>
              {isLoading ? <Spinner size="sm" /> : 'Update Address'}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default PurchaseDetailAddAddressModal
